import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SelectData } from "../../../../../../../../../models";
import { updateDeal } from "../../../../../../../../../service/fund";
import { getDeal, GetWorkflowTemplateResponse, searchPipelineDeals } from "../../../../../../../../../service/query";
import { ServiceError, Status } from "../../../../../../../../../service/Shared";
import { applyWorkflowTemplate } from "../../../../../../../../../service/workflow/WorkflowService.full";
import { useAuth } from "../../../../../../../../../useAuth";
import {
  getDealDateValidationErrorFieldKeys,
  normaliseWorkflowTemplatePhases,
  parseWorkflowStageDates,
  toDateOnlyString,
  updateWorkflowStageDates,
  useIsLoadingWrapper,
} from "../../../../../../../../../utils";
import { getWorkflowTasksBoardRoute } from "../../../../../../../../../utils/routes";
import { Toast } from "../../../../../../../../../widget";
import { DealApplyPhase, DealApplyStageDateMap, UseDealApplyWorkflowTemplatePanelReturnData } from "./models/Types";

interface UseDealApplyWorkflowTemplatePanelProps {
  defaultDealUuid?: string;
  onClose: () => void;
  workflowTemplate: GetWorkflowTemplateResponse | undefined;
  workflowTemplateUuid: string | undefined;
}

export const useDealApplyWorkflowTemplatePanel = ({
  defaultDealUuid,
  onClose,
  workflowTemplate,
  workflowTemplateUuid,
}: UseDealApplyWorkflowTemplatePanelProps): UseDealApplyWorkflowTemplatePanelReturnData => {
  const navigate = useNavigate();
  const { currentOrganisationUuid, currentUserType } = useAuth();

  const [errors, setErrors] = useState<ServiceError[]>([]);
  const [dealOptions, setDealOptions] = useState<SelectData>([]);
  const [selectedDealUuid, setSelectedDealUuid] = useState<string>();
  const [stageDates, setStageDates] = useState<DealApplyStageDateMap>({});
  const [dealDatesByUuid, setDealDatesByUuid] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);

  const orderedDealPhases = useMemo<DealApplyPhase[]>(
    () => normaliseWorkflowTemplatePhases(workflowTemplate?.phases),
    [workflowTemplate?.phases]
  );
  const errorFieldKeys = useMemo(() => getDealDateValidationErrorFieldKeys(errors), [errors]);

  const handleClearSelection = useCallback((): void => {
    setErrors([]);
    setSelectedDealUuid(undefined);
    setStageDates({});
  }, []);

  const handleClose = (): void => {
    handleClearSelection();
    onClose();
  };

  const handleSearchDeals = useCallback(
    async (search: string): Promise<void> => {
      if (!currentOrganisationUuid) {
        setDealOptions([]);
        return;
      }

      const response = await searchPipelineDeals({
        organisationUuid: currentOrganisationUuid,
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 20,
        },
        filter: {
          results: {
            name: {
              operator: "startsWith",
              value: search,
            },
            status: {
              operator: "eq",
              value: "Active",
            },
          },
        },
      });

      if (response.status !== Status.Success || !response.data) {
        setDealOptions([]);
        return;
      }

      setDealOptions(response.data.results.map((deal) => ({ key: deal.uuid, value: deal.name })));
      setDealDatesByUuid(
        response.data.results.reduce<Record<string, unknown>>(
          (result, deal) => ({
            ...result,
            [deal.uuid]: deal.dealDates,
          }),
          {}
        )
      );
    },
    [currentOrganisationUuid]
  );

  useEffect(() => {
    const loadDeals = async (): Promise<void> => {
      try {
        await handleSearchDeals("");
      } catch (error) {
        Toast.error({ message: error instanceof Error ? error.message : "Unable to load deals" });
      }
    };

    loadDeals();
  }, [handleSearchDeals]);

  useEffect(() => {
    if (!defaultDealUuid) {
      return;
    }

    setSelectedDealUuid(defaultDealUuid);
    setErrors([]);
    setStageDates(parseWorkflowStageDates(dealDatesByUuid[defaultDealUuid], orderedDealPhases));
  }, [defaultDealUuid, dealDatesByUuid, orderedDealPhases]);

  const handleSelectDeal = (dealUuid: string): void => {
    setSelectedDealUuid(dealUuid || undefined);
    setErrors([]);
    setStageDates(parseWorkflowStageDates(dealDatesByUuid[dealUuid], orderedDealPhases));
  };

  const handleStageDateChange = (phase: string, keyDate: string, value: Date | undefined): void => {
    setStageDates((previousStageDates) =>
      updateWorkflowStageDates(previousStageDates, orderedDealPhases, phase, keyDate, value)
    );
  };

  const handleSave = useIsLoadingWrapper(async (): Promise<void> => {
    if (!selectedDealUuid || !workflowTemplateUuid) {
      Toast.warning({ message: "Please select a deal" });
      return;
    }

    setErrors([]);

    const dealResponse = await getDeal({ dealUuid: selectedDealUuid });
    if (dealResponse.status !== Status.Success || !dealResponse.data) {
      setErrors([{ code: "DEAL_LOAD_FAILED", message: "Unable to load the selected deal." }]);
      return;
    }

    const parsedDealDates =
      typeof dealResponse.data.dealDates === "string"
        ? (JSON.parse(dealResponse.data.dealDates) as Record<string, Record<string, string | null | undefined>>)
        : ((dealResponse.data.dealDates ?? {}) as Record<string, Record<string, string | null | undefined>>);

    const mergedDealDates = Object.entries(parsedDealDates).reduce<
      Record<string, Record<string, string | null> | null>
    >((result, [stageKey, value]) => {
      if (!value) {
        return result;
      }

      return {
        ...result,
        [stageKey]: Object.entries(value).reduce<Record<string, string | null>>(
          (stageResult, [fieldKey, fieldValue]) => ({
            ...stageResult,
            [fieldKey]: fieldValue ?? null,
          }),
          {}
        ),
      };
    }, {});

    orderedDealPhases.forEach((phaseItem, index) => {
      const nextPhaseDates = stageDates[phaseItem.phase];
      const existingPhaseDates = mergedDealDates[phaseItem.phase];
      const mergedPhaseDates = phaseItem.keyDates.reduce<Record<string, string | null>>((phaseResult, keyDate) => {
        const nextDateValue = toDateOnlyString(nextPhaseDates?.[keyDate]) ?? undefined;
        return {
          ...phaseResult,
          [keyDate]: nextDateValue ?? existingPhaseDates?.[keyDate] ?? null,
        };
      }, {});

      if (Object.values(mergedPhaseDates).some((value) => value !== null)) {
        mergedDealDates[phaseItem.phase] = {
          ...existingPhaseDates,
          ...mergedPhaseDates,
          status: existingPhaseDates?.status ?? (index === 0 ? "In progress" : "Not started"),
        };
      }
    });

    const applyWorkflowTemplateResponse = await updateDeal({
      conversionProbability: dealResponse.data.conversionProbability,
      dealDates: mergedDealDates as unknown as Parameters<typeof updateDeal>[0]["dealDates"],
      dealSpecificData: dealResponse.data.dealSpecificData as unknown as Parameters<
        typeof updateDeal
      >[0]["dealSpecificData"],
      dealTeam: dealResponse.data.usersInvolved.map((member) => member.uuid),
      dealUuid: dealResponse.data.uuid,
      description: dealResponse.data.description ?? "",
      estimatedFees: dealResponse.data.estimatedFees ?? 0,
      introductionDate: dealResponse.data.introductionDate,
      leadUserUuid: dealResponse.data.leadUser?.uuid ?? "",
      name: dealResponse.data.name,
      projectedDpi: dealResponse.data.projectedDpi,
      relatedObjects:
        dealResponse.data.relatedObjects?.map((relatedObject) => ({
          objectType: relatedObject.objectType,
          objectUuid: relatedObject.objectUuid,
        })) ?? [],
      rowVersion: dealResponse.data.rowVersion,
      source: dealResponse.data.source,
      stage: dealResponse.data.stage,
      status: dealResponse.data.status,
      targetCloseDate: dealResponse.data.targetCloseDate ?? null,
      IsTemplateSelected: true,
    }).then(async (updateDealResponse) => {
      if (updateDealResponse.status === Status.Error) {
        setErrors(
          updateDealResponse.errors ?? [{ code: "DEAL_DATES_SAVE_FAILED", message: "Unable to save deal stage dates." }]
        );
        return undefined;
      }

      return applyWorkflowTemplate({
        objectUuid: selectedDealUuid,
        organisations: [
          {
            accessLevel: "Owner",
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            organisationUuid: currentOrganisationUuid!, // In default, the current user's organisation should have Owner access level to the deal workflow
          },
        ],
        workflowTemplateUuid,
      });
    });

    if (!applyWorkflowTemplateResponse) {
      return;
    }

    if (applyWorkflowTemplateResponse.status === Status.Error) {
      setErrors(
        applyWorkflowTemplateResponse.errors ?? [
          { code: "WORKFLOW_TEMPLATE_APPLY_FAILED", message: "Unable to apply workflow template." },
        ]
      );
      return;
    }

    if (applyWorkflowTemplateResponse.status === Status.Success && applyWorkflowTemplateResponse.data) {
      Toast.success({ message: "Workflow template successfully applied" });
      handleClose();
      navigate(getWorkflowTasksBoardRoute(applyWorkflowTemplateResponse.data.workflowUuid, currentUserType));
    }
  }, setIsSaving);

  return {
    dealOptions,
    errorFieldKeys,
    errors,
    handleClearSelection,
    handleClose,
    handleSave,
    handleSearchDeals,
    handleSelectDeal,
    handleStageDateChange,
    isSaving,
    orderedDealPhases,
    selectedDealUuid,
    stageDates,
  };
};
